import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { S3 } from 'aws-sdk';
import * as AWS from 'aws-sdk';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { Observable, Subject } from 'rxjs';

interface S3Credentials {
    accessKey: string;
    secretKey: string;
    sessionToken: string;
    expiration: Date;
}

@Injectable({
    providedIn: 'root'
})
export class FileUploaderService {

    expiredTime: number = 1000 * 60 * 30; // 30 mins
    credentials: S3Credentials;
    s3: S3;
    uploadManagedMap: Map<String, S3.ManagedUpload> = new Map();
    notification = new Subject<any>();

    constructor(private http: HttpClient) {
        AWS.config.update({
            region: environment.s3BucketRegion,
            signatureVersion: 'v4',
            useAccelerateEndpoint: environment.s3UseAccelerateEndpoint,
            maxRetries: 3
        });
        window.addEventListener('beforeunload', (ev: BeforeUnloadEvent) => {
            if (this.uploadManagedMap.size > 0) {
                ev.preventDefault();
                ev.returnValue = '';
            }
        });
        // probably won't need
        window.addEventListener('unload', () => {
            if (this.uploadManagedMap.size > 0) {
                this.uploadManagedMap.forEach((task: S3.ManagedUpload) => {
                    task.abort();
                });
            }
        });
    }

    checkAndRefreshCredentials(): Promise<any> {
        let now: Date = new Date();
        let endpoint = environment.s3TemporaryCredentialsEndpoint;
        if (!this.credentials || this.credentials.expiration.getTime() < now.getTime() || this.credentials.expiration.getTime() - now.getTime() <= this.expiredTime) {
            return this.http.get(endpoint).toPromise().then(
                (value: any) => {
                    this.credentials = {
                        accessKey: value.body.accessKey,
                        secretKey: value.body.secretKey,
                        sessionToken: value.body.sessionToken,
                        expiration: new Date(value.body.expiration)
                    };
                    let newCredentials = new AWS.Credentials({
                        accessKeyId: this.credentials.accessKey,
                        secretAccessKey: this.credentials.secretKey,
                        sessionToken: this.credentials.sessionToken
                    });
                    this.s3 = new AWS.S3({
                        credentials: newCredentials
                    });
                    return this.credentials;
                },
                (error: any) => {
                    console.error('Error on fetching new s3 temporary credentials: ', error);
                    return Promise.reject(error);
                });
        } else {
            return Promise.resolve(this.credentials);
        }
    }

    uploadFiles(files: Array<File>, callBack?: (data: Array<S3.ManagedUpload.SendData | null>) => any): Promise<Array<S3.ManagedUpload>> {
        return this.checkAndRefreshCredentials().then(() => {
            let allPromises = [];
            let allUploads = [];
            for (let file of files) {
                // assuming all files validated have extension
                let key: string = environment.s3BucketFilePath + uuidv4() + '.' + file.name.split('.').pop();
                let upload = this.s3.upload(
                    {
                        ACL: 'bucket-owner-full-control',
                        Body: file,
                        Bucket: environment.s3BucketName,
                        ContentType: file.type,
                        Key: key,
                        Metadata: {
                            fileName: file.name
                        }
                    }
                );
                allUploads.push(upload);
                allPromises.push(upload.promise().then(
                    (data: S3.ManagedUpload.SendData) => {
                        this.uploadManagedMap.delete(key);
                        return data;
                    },
                    (err: any) => {
                        console.error('Error on uploading creative file ' + key + ' to s3: ', err);
                        this.uploadManagedMap.delete(key);
                        return null;
                    }
                ));
                this.uploadManagedMap.set(key, upload);
            }

            if (callBack) {
                Promise.all(allPromises).then(
                    (value: Array<S3.ManagedUpload.SendData | null>) => {
                        // value will be a list of either S3.ManagedUpload.SendData or null, null means the upload failed
                        callBack(value);
                    }
                );
            }
            return allUploads;
        });
    }

    cancelUpload(key: string) {
        if (this.uploadManagedMap.has(key)) {
            this.uploadManagedMap.get(key).abort();
            this.uploadManagedMap.delete(key);
        }
    }

    getNotification(): Observable<any> {
        return this.notification.asObservable();
    }

    sendNotification() {
        this.notification.next(true);
    }

}